import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  Field,
  StatusBadge,
  TableIdentity,
  type DataTableColumn,
} from "@h2j-data-team/ui";
import type { AnalysisCategory, AppAnalysisFinding, AppAnalysisHealth } from "@repo/shared";

import { formatBytes, severityRank, severityTone } from "@/lib/health-format";

import { ReadOnlyTable } from "./read-only-table";

interface HealthFindingsListProps {
  health: AppAnalysisHealth;
}

export function HealthFindingsList({ health }: HealthFindingsListProps) {
  const { t } = useTranslation("analyses");
  const [showLowConfidence, setShowLowConfidence] = useState(false);

  const visibleFindings = useMemo(
    () =>
      showLowConfidence ? health.findings : health.findings.filter((f) => f.confidence !== "low"),
    [health.findings, showLowConfidence],
  );

  // Grouped by category, the groups with the worst finding first, and the worst
  // findings first inside each group.
  const rows = useMemo(() => {
    const map = new Map<AnalysisCategory, AppAnalysisFinding[]>();
    for (const f of visibleFindings) {
      const list = map.get(f.category) ?? [];
      list.push(f);
      map.set(f.category, list);
    }

    const groups = Array.from(map.entries()).map(([category, findings]) => {
      const sorted = [...findings].sort((a, b) => {
        const diff = severityRank(b.severity) - severityRank(a.severity);
        return diff !== 0 ? diff : a.id.localeCompare(b.id);
      });
      return {
        category,
        findings: sorted,
        rank: sorted.length ? severityRank(sorted[0].severity) : 0,
      };
    });

    groups.sort((a, b) => {
      const diff = b.rank - a.rank;
      return diff !== 0 ? diff : a.category.localeCompare(b.category);
    });

    return groups.flatMap((g) => g.findings);
  }, [visibleFindings]);

  const columns: DataTableColumn<AppAnalysisFinding>[] = [
    {
      id: "severity",
      header: t("findings.colSeverity"),
      width: 120,
      cell: (finding) => (
        <StatusBadge tone={severityTone(finding.severity)}>
          {t(`severity.${finding.severity}`)}
        </StatusBadge>
      ),
    },
    {
      id: "finding",
      header: t("findings.colFinding"),
      rowHeader: true,
      variant: "identity",
      cell: (finding) => (
        <TableIdentity
          name={t(`findings.${finding.id}.title`)}
          detail={findingDetail(finding, t)}
        />
      ),
    },
    {
      id: "category",
      header: t("findings.colCategory"),
      width: 190,
      cell: (finding) => t(`category.${finding.category}`),
    },
    {
      id: "confidence",
      header: t("findings.colConfidence"),
      width: 160,
      cell: (finding) => t(`confidence.${finding.confidence}`),
    },
  ];

  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[11px] font-bold tracking-[0.6px] text-faint uppercase">
          {t("findings.title")}
          <span className="ml-1 font-mono text-faint">· {visibleFindings.length}</span>
        </h2>
        <Field inline label={t("findings.showLowConfidence")}>
          <Checkbox
            size="compact"
            checked={showLowConfidence}
            onCheckedChange={(v) => setShowLowConfidence(v === true)}
          />
        </Field>
      </div>
      <ReadOnlyTable
        label={t("findings.title")}
        columns={columns}
        rows={rows}
        getRowId={(finding) => finding.id}
        empty={t("findings.empty")}
      />
    </section>
  );
}

/** Counts and bytes of a finding, then a short preview of the objects it points at. */
function findingDetail(
  finding: AppAnalysisFinding,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  // Refs preview: "FirstRefLabel, SecondRefLabel, +N more"
  const refsPreview = (() => {
    if (finding.refs.length === 0) return null;
    if (finding.refs.length === 1) return finding.refs[0].label;
    if (finding.refs.length === 2) return `${finding.refs[0].label}, ${finding.refs[1].label}`;
    return t("findings.refsPreview", {
      first: finding.refs[0].label,
      second: finding.refs[1].label,
      rest: finding.refs.length - 2,
    });
  })();

  // Compact and truthful: counts + bytes if present.
  const metricBits: string[] = [];
  if (typeof finding.metrics.fieldCount === "number") {
    metricBits.push(t("findings.metric.fields", { count: finding.metrics.fieldCount as number }));
  }
  if (typeof finding.metrics.count === "number") {
    metricBits.push(t("findings.metric.items", { count: finding.metrics.count as number }));
  }
  if (typeof finding.metrics.bytes === "number") {
    metricBits.push(formatBytes(finding.metrics.bytes as number));
  }
  return [metricBits.join(" · "), refsPreview].filter(Boolean).join(" · ");
}
